// Per-second loss strip (owned by the web:charts agent).
//
// A compact stacked-bar strip: one bar per elapsed second, stacking upstream
// loss, downstream loss, and late packets. Bars are scaled to the busiest
// second so a spike is visible even when the overall loss rate is low. Canvas
// keeps it cheap for long runs. Hovering reads out the exact counts.

import { useCallback, useMemo, useState } from 'react';
import type { RunResult } from '@/engine/types';
import { CanvasSurface, ChartLegend, EmptyNote } from './Canvas';
import { useChartTheme, type ChartPalette } from './palette';

export interface LossOverTimeProps {
  run: RunResult;
  height?: number;
}

interface Bucket {
  lostUp: number;
  lostDown: number;
  late: number;
  sent: number;
}

function bucketize(run: RunResult): Bucket[] {
  const seconds = Math.max(1, Math.ceil(run.settings.durationMs / 1000));
  const buckets: Bucket[] = Array.from({ length: seconds }, () => ({
    lostUp: 0,
    lostDown: 0,
    late: 0,
    sent: 0,
  }));
  for (const p of run.packets) {
    if (p.cls === 'notSent') continue;
    const s = Math.min(seconds - 1, Math.max(0, Math.floor((p.sentUs - run.startedAtUs) / 1e6)));
    const b = buckets[s];
    b.sent += 1;
    if (p.cls === 'lostUp' || p.cls === 'unknown') b.lostUp += 1;
    else if (p.cls === 'lostDown') b.lostDown += 1;
    else if (p.cls === 'late') b.late += 1;
  }
  return buckets;
}

export function LossOverTime({ run, height = 120 }: LossOverTimeProps) {
  const { palette } = useChartTheme();
  const buckets = useMemo(() => bucketize(run), [run]);
  const [hover, setHover] = useState<number | null>(null);

  const maxStack = useMemo(
    () => Math.max(1, ...buckets.map((b) => b.lostUp + b.lostDown + b.late)),
    [buckets],
  );

  const padL = 4;
  const padR = 4;
  const axisH = 16;

  const draw = useCallback(
    (ctx: CanvasRenderingContext2D, w: number, h: number) => {
      drawStrip(ctx, w, h, buckets, maxStack, palette, padL, padR, axisH, hover);
    },
    [buckets, maxStack, palette, hover],
  );

  const onPointer = useCallback(
    (x: number, _y: number, w: number) => {
      const usable = w - padL - padR;
      const idx = Math.floor(((x - padL) / usable) * buckets.length);
      setHover(idx >= 0 && idx < buckets.length ? idx : null);
    },
    [buckets.length],
  );

  const total = useMemo(
    () =>
      buckets.reduce(
        (a, b) => ({
          lostUp: a.lostUp + b.lostUp,
          lostDown: a.lostDown + b.lostDown,
          late: a.late + b.late,
        }),
        { lostUp: 0, lostDown: 0, late: 0 },
      ),
    [buckets],
  );

  if (buckets.length === 0) return <EmptyNote label="No per-second data." />;

  const hb = hover != null ? buckets[hover] : null;

  return (
    <figure style={{ margin: 0 }}>
      <div style={{ overflowX: 'auto' }}>
        <CanvasSurface
          height={height}
          draw={draw}
          ariaLabel="Loss and late packets per second"
          onPointer={onPointer}
          onPointerLeave={() => setHover(null)}
        />
      </div>
      <div style={{ minHeight: 18, fontSize: 12, color: palette.textMuted, marginTop: 2 }}>
        {hb
          ? `Second ${hover}: ${hb.lostUp} up, ${hb.lostDown} down, ${hb.late} late of ${hb.sent} sent`
          : `Totals: ${total.lostUp} upstream, ${total.lostDown} downstream, ${total.late} late`}
      </div>
      <ChartLegend
        items={[
          { label: 'Lost upstream', color: palette.lostUp },
          { label: 'Lost downstream', color: palette.lostDown },
          { label: 'Late', color: palette.late },
        ]}
      />
    </figure>
  );
}

function drawStrip(
  ctx: CanvasRenderingContext2D,
  w: number,
  h: number,
  buckets: Bucket[],
  maxStack: number,
  palette: ChartPalette,
  padL: number,
  padR: number,
  axisH: number,
  hover: number | null,
) {
  const usable = w - padL - padR;
  const plotH = h - axisH;
  const bw = usable / buckets.length;
  const gap = bw > 4 ? 1 : 0;

  ctx.fillStyle = palette.grid;
  ctx.fillRect(padL, plotH, usable, 1);

  buckets.forEach((b, i) => {
    const x = padL + i * bw;
    if (hover === i) {
      ctx.fillStyle = palette.grid;
      ctx.fillRect(x, 0, bw, plotH);
    }
    let y = plotH;
    const seg = (count: number, color: string) => {
      if (count <= 0) return;
      const segH = (count / maxStack) * (plotH - 2);
      y -= segH;
      ctx.fillStyle = color;
      ctx.fillRect(x + gap, y, Math.max(1, bw - gap * 2), segH);
    };
    seg(b.late, palette.late);
    seg(b.lostDown, palette.lostDown);
    seg(b.lostUp, palette.lostUp);
  });

  // Sparse second labels.
  ctx.fillStyle = palette.textMuted;
  ctx.font = '10px system-ui, sans-serif';
  ctx.textBaseline = 'top';
  const step = Math.max(1, Math.round(buckets.length / Math.max(1, Math.floor(usable / 40))));
  for (let i = 0; i < buckets.length; i += step) {
    const x = padL + i * bw;
    ctx.textAlign = 'left';
    ctx.fillText(`${i}s`, x + 1, plotH + 3);
  }
}
